import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ListData, RecommendedCarousel, Category } from '../../../types';
import useAuth from '../contexts/useAuth';

// Components
import Logo from '../components/Logo';
import BackButton from '../components/BackButton';
import BlurFillImage from '../components/BlurFillImage';
import BottomDrawer from '../components/BottomDrawer';
import Carousel from '../components/Carousel';
import CategoryPicker from '../components/CategoryPicker';
import ControlButton from '../components/ControlButton';
import { Dropdown, DropdownSelector } from '../components/Dropdown';
import Greeting from '../components/Greeting';
import ItemMeta from '../components/ItemMeta';
import ItemRow from '../components/ItemRow';
import ItemRowSkeleton from '../components/ItemRowSkeleton';
import Modal from '../components/Modal';
import NavBar from '../components/NavBar';
import { PlusButton, ProButton } from '../components/PlanButtons';
import PlayBar from '../components/PlayBar';
import PlaylistChip from '../components/PlaylistChip';
import ProgressBar from '../components/ProgressBar';
import ShareButton from '../components/ShareButton';
import SignInWithEmailButton from '../components/SignInWithEmailButton';
import SignInWithGoogleButton from '../components/SignInWithGoogleButton';
import VoiceSelector from '../components/VoiceSelector';
import WelcomeModal from '../components/WelcomeModal';
import LimitModal from '../components/LimitModal';

// Icons
import BookIcon from '../components/icons/BookIcon';
import CheckIcon from '../components/icons/CheckIcon';
import ChevronLeft from '../components/icons/ChevronLeft';
import ExternalLinkIcon from '../components/icons/ExternalLinkIcon';
import FastForwardIcon from '../components/icons/FastForwardIcon';
import HistoryIcon from '../components/icons/HistoryIcon';
import ListIcon from '../components/icons/ListIcon';
import MinusIcon from '../components/icons/MinusIcon';
import PencilIcon from '../components/icons/PencilIcon';
import PlayIcon from '../components/icons/PlayIcon';
import PlusIcon from '../components/icons/PlusIcon';
import PlusIcon2 from '../components/icons/PlusIcon2';
import Profile from '../components/icons/Profile';
import QueueIcon from '../components/icons/QueueIcon';
import ShareIcon from '../components/icons/ShareIcon';
import SkipNextIcon from '../components/icons/SkipNextIcon';
import StarIcon from '../components/icons/StarIcon';
import TrashIcon from '../components/icons/TrashIcon';

// --- Mock data ---

const mockContent: ListData = {
  id: 'mock-1',
  type: 'content',
  title: 'The Age of the Essay',
  description: 'An essay about essays.',
  sourceUrl: 'https://paulgraham.com/essay.html',
  imageUrl: 'https://picsum.photos/seed/earmark1/200/200',
  author: 'Paul Graham',
  status: 'completed',
  secondsTotal: 1200,
  secondsListened: 300,
  secondsGenerated: 1200,
  favorite: false,
  inQueue: 1,
  queuePosition: 1,
};

const mockPlaylist: ListData = {
  id: 'mock-pl-1',
  type: 'playlist',
  title: 'Paul Graham Essays',
  description: 'A collection of PG essays.',
  imageUrl: 'https://picsum.photos/seed/earmark2/200/200',
  status: 'completed',
  contentIds: JSON.stringify(['a', 'b', 'c', 'd', 'e']),
  playlistPosition: 2,
  favorite: false,
  inQueue: 1,
  queuePosition: 2,
};

const mockCarouselItem = (
  id: string,
  title: string,
  seed: string
): ListData => ({
  id,
  type: 'content',
  title,
  description: null,
  imageUrl: `https://picsum.photos/seed/${seed}/200/200`,
  favorite: false,
  inQueue: 0,
  queuePosition: null,
});

const mockCarousel: RecommendedCarousel = {
  type: 'carousel',
  title: 'Popular This Week',
  link: '/category/popular',
  items: [
    mockCarouselItem('1', 'How to Do Great Work', 'c1'),
    mockCarouselItem('2', 'The Bitter Lesson', 'c2'),
    mockCarouselItem('3', 'Meditations on Moloch', 'c3'),
    mockCarouselItem('4', "What You Can't Say", 'c4'),
  ],
};

const mockCategories: Category[] = [
  { id: 'cat-1', name: 'Technology', createdAt: '2024-01-01' },
  { id: 'cat-2', name: 'Philosophy', createdAt: '2024-01-01' },
  { id: 'cat-3', name: 'Science', createdAt: '2024-01-01' },
  { id: 'cat-4', name: 'Economics', createdAt: '2024-01-01' },
  { id: 'cat-5', name: 'AI & ML', createdAt: '2024-01-01' },
  { id: 'cat-6', name: 'History', createdAt: '2024-01-01' },
];

// --- Section wrapper ---

const Section = ({
  title,
  children,
  note,
}: {
  title: string;
  children: React.ReactNode;
  note?: string;
}) => (
  <div style={{ marginBottom: '40px' }}>
    <h2
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 400,
        fontSize: '1.3rem',
        color: 'var(--primary)',
        margin: '0 0 4px 0',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '8px',
      }}
    >
      {title}
    </h2>
    {note && (
      <p
        style={{
          fontSize: '0.8rem',
          color: 'var(--primary-soft)',
          margin: '4px 0 12px 0',
          fontStyle: 'italic',
        }}
      >
        {note}
      </p>
    )}
    <div style={{ marginTop: note ? 0 : '12px' }}>{children}</div>
  </div>
);

const Row = ({
  children,
  gap = '12px',
}: {
  children: React.ReactNode;
  gap?: string;
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap,
      flexWrap: 'wrap',
    }}
  >
    {children}
  </div>
);

// --- Page ---

export default function Stylesheet() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  // State for interactive demos
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [limitSigninOpen, setLimitSigninOpen] = useState(false);
  const [limitUpgradeOpen, setLimitUpgradeOpen] = useState(false);
  const [voice, setVoice] = useState('openai-onyx');
  const [dropdownValue, setDropdownValue] = useState('option1');

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <div className="page" style={{ padding: '20px 16px 120px 16px' }}>
      <p className="section-title" style={{ margin: '10px 0 30px 0' }}>
        Component Stylesheet
      </p>

      {/* ===== DESIGN TOKENS ===== */}
      <Section title="Design Tokens — Colors">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '8px',
          }}
        >
          {[
            ['--background', 'Background'],
            ['--background-alt', 'Background Alt'],
            ['--primary', 'Primary'],
            ['--primary-soft', 'Primary Soft'],
            ['--accent', 'Accent / Gold'],
            ['--accent-hover', 'Accent Hover'],
            ['--accent-soft', 'Accent Soft'],
            ['--teal', 'Teal'],
            ['--teal-hover', 'Teal Hover'],
            ['--teal-soft', 'Teal Soft'],
            ['--card-bg', 'Card BG'],
            ['--border', 'Border'],
            ['--border-strong', 'Border Strong'],
            ['--success', 'Success'],
            ['--error', 'Error'],
          ].map(([v, label]) => (
            <div key={v} style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: '100%',
                  height: '48px',
                  backgroundColor: `var(${v})`,
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                }}
              />
              <span
                style={{ fontSize: '0.7rem', color: 'var(--primary-soft)' }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Design Tokens — Typography">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <span
              style={{
                fontSize: '0.7rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '2px',
              }}
            >
              --font-display (Fraunces)
            </span>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.5rem',
                fontWeight: 400,
              }}
            >
              The quick brown fox jumps over the lazy dog
            </span>
          </div>
          <div>
            <span
              style={{
                fontSize: '0.7rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '2px',
              }}
            >
              --font-body (Source Sans 3)
            </span>
            <span
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
              }}
            >
              The quick brown fox jumps over the lazy dog
            </span>
          </div>
          <p className="section-title" style={{ margin: 0 }}>
            .section-title
          </p>
          <h3 className="item-title">
            .item-title — Two Line Title That Could Be Quite Long and Would
            Clamp
          </h3>
          <p className="subtle-text">.subtle-text — Secondary info</p>
          <p className="item-origin">.item-origin — paulgraham.com</p>
        </div>
      </Section>

      <Section title="Design Tokens — Shadows">
        <Row gap="16px">
          {[
            ['--shadow-sm', 'SM'],
            ['--shadow-md', 'MD'],
            ['--shadow-lg', 'LG'],
          ].map(([v, label]) => (
            <div
              key={v}
              style={{
                width: '80px',
                height: '80px',
                background: 'var(--card-bg)',
                borderRadius: '8px',
                boxShadow: `var(${v})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                color: 'var(--primary-soft)',
              }}
            >
              {label}
            </div>
          ))}
        </Row>
      </Section>

      {/* ===== ICONS ===== */}
      <Section title="Icons" note="All icons accept size and color props">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
            gap: '12px',
          }}
        >
          {[
            ['BookIcon', <BookIcon key="book" size={24} />],
            ['CheckIcon', <CheckIcon key="check" size={24} />],
            ['ChevronLeft', <ChevronLeft key="chevron" size={24} />],
            ['ExtLink', <ExternalLinkIcon key="ext" size={24} />],
            [
              'FastFwd',
              <FastForwardIcon key="ff" size={24} color="var(--primary)" />,
            ],
            ['History', <HistoryIcon key="hist" size={24} />],
            ['List', <ListIcon key="list" size={24} />],
            ['Minus', <MinusIcon key="minus" size={24} />],
            ['Pencil', <PencilIcon key="pencil" size={24} />],
            ['Play', <PlayIcon key="play" size={24} />],
            ['Plus', <PlusIcon key="plus" size={24} />],
            ['Plus2', <PlusIcon2 key="plus2" size={24} />],
            ['Profile', <Profile key="profile" />],
            ['Queue', <QueueIcon key="queue" size={24} />],
            ['Share', <ShareIcon key="share" size={24} />],
            [
              'SkipNext',
              <SkipNextIcon key="skip" size={24} color="var(--primary)" />,
            ],
            ['Star', <StarIcon key="star" size={24} color="var(--accent)" />],
            [
              'Star filled',
              <StarIcon key="starf" size={24} color="var(--accent)" filled />,
            ],
            ['Trash', <TrashIcon key="trash" size={24} />],
          ].map(([name, icon]) => (
            <div
              key={name as string}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {icon}
              <span
                style={{ fontSize: '0.6rem', color: 'var(--primary-soft)' }}
              >
                {name as string}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== BUTTONS ===== */}
      <Section title="Buttons">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              .pill-button
            </span>
            <Row>
              <button className="pill-button">Default</button>
              <button className="pill-button" disabled>
                Disabled
              </button>
            </Row>
          </div>

          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              .text-link
            </span>
            <button className="text-link">See all</button>
          </div>

          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              .play-button
            </span>
            <Row>
              <button className="play-button" style={{ width: 40, height: 40 }}>
                <PlayIcon size={28} color="var(--background)" />
              </button>
              <button
                className="play-button playing"
                style={{ width: 40, height: 40 }}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="var(--background)"
                >
                  <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
                </svg>
              </button>
              <button
                className="play-button loading"
                style={{ width: 40, height: 40 }}
              >
                <svg
                  className="spinner"
                  width={20}
                  height={20}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--background)"
                  strokeWidth="3"
                  strokeLinecap="round"
                >
                  <path d="M12 2 A10 10 0 0 1 22 12" />
                </svg>
              </button>
              <button
                className="play-button disabled"
                style={{ width: 40, height: 40 }}
              >
                <PlayIcon size={28} color="var(--background)" />
              </button>
            </Row>
          </div>

          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              ControlButton
            </span>
            <Row>
              <ControlButton>
                <FastForwardIcon size={24} color="var(--gold)" />
              </ControlButton>
              <ControlButton>
                <SkipNextIcon size={24} color="var(--gold)" />
              </ControlButton>
            </Row>
          </div>

          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              PlanButtons
            </span>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignItems: 'flex-start',
              }}
            >
              <PlusButton />
              <ProButton />
              <Row>
                <PlusButton variant="pill" />
                <ProButton variant="pill" />
              </Row>
            </div>
          </div>

          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              ShareButton
            </span>
            <ShareButton />
          </div>

          <div>
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--primary-soft)',
                display: 'block',
                marginBottom: '6px',
              }}
            >
              Auth Buttons
            </span>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxWidth: '280px',
              }}
            >
              <SignInWithGoogleButton />
              <SignInWithEmailButton />
            </div>
          </div>
        </div>
      </Section>

      {/* ===== CARDS ===== */}
      <Section title="Cards & Surfaces">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="card" style={{ padding: '16px' }}>
            <p style={{ margin: 0 }}>.card — default state (hover me)</p>
          </div>
          <div className="card active" style={{ padding: '16px' }}>
            <p style={{ margin: 0 }}>.card.active — active/selected state</p>
          </div>
        </div>
      </Section>

      {/* ===== CONTENT DISPLAY ===== */}
      <Section title="ItemRow">
        <ItemRow data={mockContent} />
        <ItemRow data={mockPlaylist} />
        <ItemRow
          data={{ ...mockContent, status: 'failed', title: 'Failed Upload' }}
        />
      </Section>

      <Section title="ItemRowSkeleton">
        <ItemRowSkeleton />
        <ItemRowSkeleton message="Scraping content..." />
      </Section>

      <Section title="ItemMeta">
        <ItemMeta item={mockContent} />
        <div style={{ marginTop: '8px' }} />
        <ItemMeta item={mockPlaylist} />
      </Section>

      <Section title="PlaylistChip">
        <PlaylistChip playlist={mockPlaylist} />
      </Section>

      <Section title="BlurFillImage">
        <Row>
          <BlurFillImage
            imageUrl="https://picsum.photos/seed/earmark1/200/200"
            style={{ width: '100px', height: '100px', borderRadius: '8px' }}
          />
          <BlurFillImage
            imageUrl={null}
            style={{ width: '100px', height: '100px', borderRadius: '8px' }}
          />
        </Row>
      </Section>

      <Section title="Carousel">
        <Carousel carousel={mockCarousel} />
      </Section>

      <Section title="Greeting">
        <Greeting />
      </Section>

      <Section title="Logo">
        <Logo />
      </Section>

      <Section
        title="BackButton"
        note="Positioned absolutely, shown in-flow here"
      >
        <div style={{ position: 'relative', height: '100px' }}>
          <BackButton />
        </div>
      </Section>

      {/* ===== INTERACTIVE CONTROLS ===== */}
      <Section title="VoiceSelector">
        <VoiceSelector voice={voice} setVoice={setVoice} />
      </Section>

      <Section title="Dropdown">
        <Row>
          <Dropdown
            renderButton={({ toggle }) => (
              <button className="pill-button" onClick={toggle}>
                Open Dropdown
              </button>
            )}
          >
            {({ close }) => (
              <div
                style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <p style={{ margin: '0 0 8px 0' }}>Custom dropdown content</p>
                <button className="pill-button" onClick={close}>
                  Close
                </button>
              </div>
            )}
          </Dropdown>

          <DropdownSelector
            value={dropdownValue}
            options={[
              { value: 'option1', label: 'Option One' },
              { value: 'option2', label: 'Option Two' },
              { value: 'option3', label: 'New Option', badge: 'New' },
            ]}
            onChange={setDropdownValue}
            renderButton={({ toggle }) => (
              <button className="pill-button" onClick={toggle}>
                Selector: {dropdownValue}
              </button>
            )}
          />
        </Row>
      </Section>

      <Section title="CategoryPicker">
        <CategoryPicker
          categories={mockCategories}
          onComplete={async (ids) => {
            alert(`Selected: ${ids.join(', ')}`);
          }}
        />
      </Section>

      {/* ===== PROGRESS ===== */}
      <Section
        title="ProgressBar"
        note="Uses player context — shows empty state here"
      >
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <ProgressBar />
        </div>
        <p
          style={{
            fontSize: '0.75rem',
            color: 'var(--primary-soft)',
            marginTop: '4px',
          }}
        >
          Static mock:
        </p>
        <div style={{ display: 'flex', width: '100%', height: '5px' }}>
          <div style={{ width: '35%', backgroundColor: 'var(--teal)' }} />
          <div style={{ width: '65%', backgroundColor: 'var(--border)' }} />
        </div>
      </Section>

      <Section title="Shimmer Loading">
        <div
          className="shimmer"
          style={{
            height: '16px',
            width: '70%',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        />
        <div
          className="shimmer"
          style={{ height: '12px', width: '40%', borderRadius: '4px' }}
        />
      </Section>

      {/* ===== LAYOUT ===== */}
      <Section
        title="NavBar"
        note="Rendered in a bordered container to show structure"
      >
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <NavBar />
        </div>
      </Section>

      <Section
        title="PlayBar"
        note="Uses player context — shows empty when nothing is playing. See bottom of page when audio is active."
      >
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <PlayBar onOpen={() => {}} />
        </div>
      </Section>

      {/* ===== OVERLAYS (trigger buttons) ===== */}
      <Section title="Overlays & Modals" note="Click buttons to preview">
        <Row>
          <button className="pill-button" onClick={() => setModalOpen(true)}>
            Modal
          </button>
          <button className="pill-button" onClick={() => setDrawerOpen(true)}>
            BottomDrawer
          </button>
          <button className="pill-button" onClick={() => setWelcomeOpen(true)}>
            WelcomeModal
          </button>
          <button
            className="pill-button"
            onClick={() => setLimitSigninOpen(true)}
          >
            LimitModal (signin)
          </button>
          <button
            className="pill-button"
            onClick={() => setLimitUpgradeOpen(true)}
          >
            LimitModal (upgrade)
          </button>
        </Row>
      </Section>

      {/* ===== PAGES (links) ===== */}
      <Section title="Pages" note="Navigate to view full page components">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
          }}
        >
          {[
            ['/', 'Home'],
            ['/queue', 'Queue'],
            ['/history', 'History'],
            ['/favorites', 'Favorites'],
            ['/profile', 'Profile'],
            ['/admin', 'Admin'],
            ['/paulgraham', 'Paul Graham'],
            ['/waitbutwhy', 'Wait But Why'],
            ['/gwern', 'Gwern'],
            ['/accelerando', 'Accelerando'],
          ].map(([path, name]) => (
            <a
              key={path}
              href={path}
              style={{
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                textDecoration: 'none',
                color: 'var(--accent)',
                fontSize: '0.9rem',
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              {name}
            </a>
          ))}
        </div>
      </Section>

      {/* ===== OVERLAY INSTANCES ===== */}
      {modalOpen && (
        <Modal onBackdropClick={() => setModalOpen(false)}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 400,
              color: 'var(--primary)',
              marginBottom: '12px',
            }}
          >
            Example Modal
          </h2>
          <p style={{ color: 'var(--primary-soft)', marginBottom: '16px' }}>
            This is the generic Modal component with custom content.
          </p>
          <button className="pill-button" onClick={() => setModalOpen(false)}>
            Close
          </button>
        </Modal>
      )}

      <BottomDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 400,
            marginBottom: '12px',
          }}
        >
          Bottom Drawer
        </h3>
        <p style={{ color: 'var(--primary-soft)', marginBottom: '16px' }}>
          This slides up from the bottom. Used for chapters, speed, etc.
        </p>
        <button className="pill-button" onClick={() => setDrawerOpen(false)}>
          Close
        </button>
      </BottomDrawer>

      {welcomeOpen && <WelcomeModal onDismiss={() => setWelcomeOpen(false)} />}

      {limitSigninOpen && (
        <LimitModal type="signin" onDismiss={() => setLimitSigninOpen(false)} />
      )}

      {limitUpgradeOpen && (
        <LimitModal
          type="upgrade"
          onDismiss={() => setLimitUpgradeOpen(false)}
        />
      )}
    </div>
  );
}
